import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Avatar,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
} from "@material-ui/core";
import { green, orange } from "@material-ui/core/colors";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";
import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";
import BusinessIcon from "@material-ui/icons/Business";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { resolveContactAvatar, hasCustomAvatar } from "../../utils/avatar";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    backgroundColor: theme.palette.background.paper,
    border:
      theme.palette.type === "dark"
        ? `1px solid ${theme.palette.divider}`
        : "1px solid rgba(230, 230, 230, 0.5)",
    overflow: "hidden",
    "&:hover": {
      boxShadow: "0 6px 25px 0 rgba(0, 0, 0, 0.1)",
      transform: "translateY(-4px)",
    },
  },
  cardContent: {
    height: "100%",
    padding: theme.spacing(2),
    "&:last-child": {
      paddingBottom: theme.spacing(2),
    },
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
  },
  titleIcon: {
    marginRight: theme.spacing(1.5),
    fontSize: "1.8rem",
    borderRadius: "50%",
    padding: theme.spacing(0.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(76, 175, 80, 0.1)"
        : "rgba(76, 175, 80, 0.08)",
    color: theme.palette.success.main,
  },
  title: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: "0.01em",
  },
  statChip: {
    height: 24,
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
  tableContainer: {
    flex: 1,
    height: "calc(100% - 48px)",
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(0,0,0,0.1)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.main,
      borderRadius: "10px",
    },
  },
  tableCell: {
    padding: theme.spacing(0.75),
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
    fontSize: "0.8rem",
  },
  tableCellHead: {
    padding: theme.spacing(0.75),
    fontWeight: "bold",
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.default
        : theme.palette.grey[100],
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  tableCellLeft: {
    padding: theme.spacing(0.75),
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: "left",
    fontSize: "0.8rem",
  },
  avatar: {
    width: 28,
    height: 28,
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: "0.7rem",
  },
  avatarWithImage: {
    width: 28,
    height: 28,
    border: `1px solid ${theme.palette.divider}`,
  },
  contactInfo: {
    display: "flex",
    alignItems: "center",
  },
  contactName: {
    marginLeft: theme.spacing(1),
    fontWeight: 500,
    fontSize: "0.85rem",
  },
  iconCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
  },
  cellIcon: {
    marginRight: theme.spacing(0.5),
    color: theme.palette.primary.main,
    fontSize: "1rem",
  },
  queueIcon: {
    marginRight: theme.spacing(0.5),
    color: orange[500],
    fontSize: "1rem",
  },
  timeIcon: {
    marginRight: theme.spacing(0.5),
    color: green[500],
    fontSize: "1rem",
  },
  noContactsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "250px",
    width: "100%",
  },
  noContactsText: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "250px",
    width: "100%",
  },
}));

const TopContactsTable = ({ dateFrom, dateTo }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/dashboard/top-contacts", {
          params: {
            date_from: dateFrom,
            date_to: dateTo,
          },
        });
        if (isMounted) {
          setContacts(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      } catch (error) {
        // Silently handle 404 errors (route not implemented)
        if (error?.response?.status !== 404) {
          console.error("Error fetching top contacts", error);
        }
        if (isMounted) {
          setContacts([]);
          setLoading(false);
        }
      }
    };

    if (dateFrom && dateTo) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [dateFrom, dateTo]);

  const formatAttendanceTime = (minutes) => {
    if (!minutes) return "0m";

    if (minutes >= 525600) {
      const years = Math.floor(minutes / 525600);
      const remainingMonths = Math.floor((minutes % 525600) / 43800);
      if (remainingMonths > 0) {
        return `${years}a ${remainingMonths}m`;
      }
      return `${years}a`;
    }

    if (minutes >= 43800) {
      const months = Math.floor(minutes / 43800);
      const remainingDays = Math.floor((minutes % 43800) / 1440);
      if (remainingDays > 0) {
        return `${months}m ${remainingDays}d`;
      }
      return `${months}m`;
    }

    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.floor((minutes % 1440) / 60);
      if (remainingHours > 0) {
        return `${days}d ${remainingHours}h`;
      }
      return `${days}d`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${remainingMinutes}m`;
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? `${text.substring(0, limit)}...` : text;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <ContactPhoneIcon className={classes.titleIcon} />
              <Typography className={classes.title}>
                {i18n.t("dashboard.topContacts.title") ||
                  "Contatos em Destaque"}
              </Typography>
            </div>
          </div>
          <div className={classes.loadingContainer}>
            <CircularProgress size={40} thickness={4} color="primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!contacts || contacts.length === 0) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <ContactPhoneIcon className={classes.titleIcon} />
              <Typography className={classes.title}>
                {i18n.t("dashboard.topContacts.title") ||
                  "Contatos em Destaque"}
              </Typography>
            </div>
          </div>
          <div className={classes.noContactsContainer}>
            <Typography className={classes.noContactsText}>
              {i18n.t("dashboard.topContacts.noData") ||
                "Sem dados disponíveis"}
            </Typography>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <div className={classes.titleContainer}>
            <ContactPhoneIcon className={classes.titleIcon} />
            <Typography className={classes.title}>
              {i18n.t("dashboard.topContacts.title") || "Contatos em Destaque"}
            </Typography>
          </div>
          <Chip
            size="small"
            label={`Top 10 contatos`}
            className={classes.statChip}
            variant="outlined"
          />
        </div>

        <TableContainer
          component={Paper}
          className={classes.tableContainer}
          variant="outlined"
        >
          <Table size="small" aria-label="top contacts table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCellHead}>
                  {i18n.t("dashboard.topContacts.columns.contact") || "Contato"}
                </TableCell>
                <TableCell align="center" className={classes.tableCellHead}>
                  {i18n.t("dashboard.topContacts.columns.tickets") || "Tickets"}
                </TableCell>
                <TableCell align="center" className={classes.tableCellHead}>
                  {i18n.t("dashboard.topContacts.columns.queue") || "Fila"}
                </TableCell>
                <TableCell align="center" className={classes.tableCellHead}>
                  {i18n.t("dashboard.topContacts.columns.time") || "Tempo"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell className={classes.tableCellLeft}>
                    <Box className={classes.contactInfo}>
                      {hasCustomAvatar(contact) ? (
                        <Avatar
                          src={resolveContactAvatar(contact)}
                          className={classes.avatarWithImage}
                        />
                      ) : (
                        <Avatar className={classes.avatar}>
                          {getInitials(contact.name)}
                        </Avatar>
                      )}
                      <Tooltip
                        title={contact.name || "Sem nome"}
                        placement="top"
                      >
                        <Typography className={classes.contactName}>
                          {truncateText(contact.name || "Sem nome", 20)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <Box className={classes.iconCell}>
                      <ConfirmationNumberIcon className={classes.cellIcon} />
                      {contact.ticketsCount}
                    </Box>
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <Box className={classes.iconCell}>
                      <BusinessIcon className={classes.queueIcon} />
                      {contact.mostCommonQueue}
                    </Box>
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <Box className={classes.iconCell}>
                      <AccessTimeIcon className={classes.timeIcon} />
                      {formatAttendanceTime(contact.totalAttendanceTime)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default TopContactsTable;
